"use client";
import { getUserType } from "@nimble/datamodel/enums/userTypes";
import Script from "next/script";
import { useEffect } from "react";

import { publicEnv } from "@/env/public-env";
import { PRODUCTION_HOSTNAME, STAGING_HOSTNAME } from "@/lib/constants";
import { User } from "@/lib/types/auth";
import { useAuth } from "@/stores/use-auth";

declare global {
  interface Window {
    pendo?: typeof pendo;
  }
}

const ATTEMPTS_MAX = 10;
const ATTEMPTS_INTERVAL = 250;

async function getPendo(): Promise<typeof pendo> {
  return new Promise((resolve, reject) => {
    const check = (attempt: number) => {
      if (window.pendo) return resolve(window.pendo);

      if (attempt <= ATTEMPTS_MAX) {
        setTimeout(() => check(attempt + 1), ATTEMPTS_INTERVAL);
      } else {
        reject("Pendo waiting max attempts reached");
      }
    };
    check(1);
  });
}

async function identifyUser(user: User | null) {
  const pendo = await getPendo();

  pendo.initialize();

  const envDetails = {
    is_staging: window.location.hostname === STAGING_HOSTNAME,
    is_prod: window.location.hostname === PRODUCTION_HOSTNAME,
  };

  if (user) {
    const userTypeLabel = getUserType(user.profile?.user_type)?.label ?? "";

    pendo.identify({
      visitor: {
        id: user.id.toString(),
        email: user.email,
        full_name: user.name,
        role: userTypeLabel,

        user_type: userTypeLabel,
        creation_date: user.date_joined,
        last_login: user.last_login,
        city: user.profile?.address_city || "",
        state: user.profile?.address_state || "",

        ...envDetails,
      },

      account: {
        id: user.profile?.district?.id?.toString() || "",
        name: user.profile?.district?.name || "",
        parent_organization: user.profile?.district?.parent_organization || "",
      },
    });
  } else {
    pendo.identify({
      visitor: {
        ...envDetails,
      },
    });
  }
}

function PendoAuthTracker() {
  const { isLoading, user } = useAuth();

  useEffect(() => {
    if (!isLoading) {
      identifyUser(user);
    }
  }, [isLoading, user]);

  return null;
}

export default function Pendo() {
  const pendoKey = publicEnv.NEXT_PUBLIC_PENDO_KEY;

  if (!pendoKey) {
    return null;
  }

  return (
    <>
      <Script src={`https://cdn.pendo.io/agent/static/${pendoKey}/pendo.js`} />
      <PendoAuthTracker />
    </>
  );
}
